import { alpha, useTheme } from '@mui/material/styles'

import { CategoryIcon, MerchantLogo, Text } from '@mxenabled/mxui'

import { buildDynamicCopy, formatCurrency } from '../../../../common'
import type { BudgetBlockData } from '../../../../core/types/InsightsInstanceSlotData'
import { getCategoryColor } from '../../../../core/utils/CategoryUtil'

import StatusIndicator from '../../../components/ComponentBlocks/StatusIndicator'

import type { BlockProps } from '../../types'

import { getColorByBudgetTier } from './util'

export const BudgetBlock = ({ payload }: BlockProps<BudgetBlockData>) => {
  const {
    budget_percent,
    amount,
    budget_tier,
    category_guid,
    merchant_guid,
    spent_amount,
    budgeted_amount,
    localized_payload,
  } = payload.instance_slot_data

  const color = getColorByBudgetTier(budget_tier)

  const theme = useTheme()

  const avatarBgColor = category_guid ? getCategoryColor(category_guid, theme) : ''

  // Precomputed as a JSX element (not a component defined during render) so it
  // isn't remounted on every re-render.
  const icon =
    category_guid && category_guid !== null ? (
      <CategoryIcon
        categoryGuid={category_guid}
        size={44}
        sx={{
          color: 'text.primary',
          bgcolor: alpha(avatarBgColor, 0.25),
          borderColor: avatarBgColor,
          borderWidth: 1,
          borderStyle: 'solid',
        }}
      />
    ) : (
      <MerchantLogo categoryGuid={category_guid || ''} merchantGuid={merchant_guid || ''} />
    )

  const block_left_footer = buildDynamicCopy(
    localized_payload?.spent_label || '',
    formatCurrency(spent_amount, '0,0.00'),
  ) as string

  const block_right_footer = buildDynamicCopy(
    localized_payload?.budgeted_label || '',
    formatCurrency(budgeted_amount, '0,0.00'),
  ) as string

  const amountToDisplay = (
    <Text bold={true} variant="h2">
      {category_guid && category_guid !== null
        ? (buildDynamicCopy(
            localized_payload?.amount_left_label || '',
            formatCurrency(amount, '0,0.00'),
          ) as string)
        : formatCurrency(amount, '0,0.00')}
    </Text>
  )

  return (
    <StatusIndicator
      amount={amountToDisplay}
      borderRadius="12px"
      completeColor={color}
      icon={icon}
      label={localized_payload?.primary_text || ''}
      leftLabel={block_left_footer}
      percentComplete={budget_percent}
      rightLabel={block_right_footer}
      sx={{ '& .details-container': { gap: 12 } }}
    />
  )
}
